import { useEffect, useState } from "react";
import { RdsRegistration, api } from "../api";
import { useAuth } from "../auth";

type Tab = "global" | "databases" | "behavior";

export function ContextPage() {
  const [tab, setTab] = useState<Tab>("global");
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold">Context</h2>
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-sm">
          <SegBtn active={tab === "global"} onClick={() => setTab("global")}>
            Global
          </SegBtn>
          <SegBtn active={tab === "databases"} onClick={() => setTab("databases")}>
            Databases
          </SegBtn>
          <SegBtn active={tab === "behavior"} onClick={() => setTab("behavior")}>
            Behavior
          </SegBtn>
        </div>
      </div>
      <p className="text-sm text-slate-600">
        Context tells Claude what these databases are and how to query them. <strong>Global</strong> applies to every
        database; <strong>Databases</strong> is a per-database manual. <strong>Behavior</strong> controls how much of it
        is auto-injected: the full manual, or a short pointer that tells Claude to fetch it on demand.
      </p>
      {tab === "global" ? <GlobalPanel /> : tab === "databases" ? <DatabasesPanel /> : <BehaviorPanel />}
    </div>
  );
}

function GlobalPanel() {
  const { token } = useAuth();
  const [value, setValue] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    if (!token) return;
    api
      .getServerContext(token)
      .then((r) => {
        setValue(r.value ?? "");
        setLoaded(true);
      })
      .catch((e) => setNotice({ tone: "err", text: String(e?.message ?? e) }));
  }, [token]);

  const onSave = async () => {
    if (!token) return;
    setBusy(true);
    setNotice(null);
    try {
      await api.putServerContext(token, value);
      setNotice({ tone: "ok", text: "Saved. Live for tools immediately; the connect-time copy refreshes on next redeploy." });
    } catch (e) {
      setNotice({ tone: "err", text: String((e as { message?: string })?.message ?? e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4 space-y-3">
      <h3 className="text-sm font-semibold text-slate-900">Global context (applies to all databases)</h3>
      <textarea
        className="input font-mono text-xs min-h-[420px]"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={loaded ? "Describe the stack, general rules, confidentiality…" : "Loading…"}
        disabled={!loaded}
      />
      {notice && <Notice tone={notice.tone}>{notice.text}</Notice>}
      <button type="button" className="btn-primary" onClick={() => void onSave()} disabled={busy || !loaded}>
        {busy ? "Saving…" : "Save global context"}
      </button>
    </div>
  );
}

function BehaviorPanel() {
  const { token } = useAuth();
  const [mode, setMode] = useState("full");
  const [useDefault, setUseDefault] = useState(true);
  const [customText, setCustomText] = useState("");
  const [defaultText, setDefaultText] = useState("");
  const [view, setView] = useState<"default" | "custom">("default");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    if (!token) return;
    api
      .getContextBehavior(token)
      .then((r) => {
        setMode(r.default_mode);
        setUseDefault(r.use_default);
        setCustomText(r.custom_pointer_text ?? "");
        setDefaultText(r.default_pointer_text ?? "");
        setView(r.use_default ? "default" : "custom");
        setLoaded(true);
      })
      .catch((e) => setNotice({ tone: "err", text: String(e?.message ?? e) }));
  }, [token]);

  const onSave = async () => {
    if (!token) return;
    setBusy(true);
    setNotice(null);
    try {
      const r = await api.putContextBehavior(token, {
        default_mode: mode,
        pointer_use_default: useDefault,
        pointer_custom_text: customText,
      });
      setMode(r.default_mode);
      setUseDefault(r.use_default);
      setCustomText(r.custom_pointer_text ?? "");
      setDefaultText(r.default_pointer_text ?? "");
      setNotice({ tone: "ok", text: "Saved. Applies to new MCP sessions immediately." });
    } catch (e) {
      setNotice({ tone: "err", text: String((e as { message?: string })?.message ?? e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4 space-y-4">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-slate-900">Default delivery mode</h3>
        <p className="text-xs text-slate-500">
          What Claude receives on the first result from each database (unless a database overrides it on the Databases
          tab).
        </p>
        <select className="input max-w-xs" value={mode} onChange={(e) => setMode(e.target.value)} disabled={!loaded}>
          <option value="full">Full — inject the whole Global + database manual</option>
          <option value="pointer">Pointer only — inject a short note; Claude fetches the manual on demand</option>
        </select>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-900">Pointer text</h3>
        <p className="text-xs text-slate-500">
          Used in <strong>pointer</strong> mode. Placeholders <code>{"{rds_id}"}</code> and{" "}
          <code>{"{display_name}"}</code> are substituted per database.
        </p>

        <div className="flex items-center gap-4 text-sm">
          <label className="inline-flex items-center gap-1.5">
            <input type="radio" name="pointer-active" checked={useDefault} onChange={() => setUseDefault(true)} disabled={!loaded} />
            Use default
          </label>
          <label className="inline-flex items-center gap-1.5">
            <input type="radio" name="pointer-active" checked={!useDefault} onChange={() => setUseDefault(false)} disabled={!loaded} />
            Use custom
          </label>
          <span className="text-xs text-slate-400">
            Active: <strong className="text-slate-600">{useDefault ? "default" : "custom"}</strong>
          </span>
        </div>

        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-sm">
          <SegBtn active={view === "default"} onClick={() => setView("default")}>
            Default (read-only)
          </SegBtn>
          <SegBtn active={view === "custom"} onClick={() => setView("custom")}>
            Custom (editable)
          </SegBtn>
        </div>

        {view === "default" ? (
          <pre className="text-[11px] font-mono whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded p-3 min-h-[140px]">
            {defaultText || "Loading…"}
          </pre>
        ) : (
          <textarea
            className="input font-mono text-xs min-h-[140px]"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            placeholder={loaded ? "Context for {display_name} (rds_id={rds_id}) is loaded. Call read_database_context({rds_id})…" : "Loading…"}
            disabled={!loaded}
          />
        )}
        {view === "custom" && !customText.trim() && (
          <p className="text-xs text-amber-700">Custom text is empty — delivery falls back to the built-in default.</p>
        )}
      </div>

      {notice && <Notice tone={notice.tone}>{notice.text}</Notice>}
      <button type="button" className="btn-primary" onClick={() => void onSave()} disabled={busy || !loaded}>
        {busy ? "Saving…" : "Save behavior"}
      </button>
    </div>
  );
}

function DatabasesPanel() {
  const { token } = useAuth();
  const [rds, setRds] = useState<RdsRegistration[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<RdsRegistration | null>(null);

  const reload = () => {
    if (!token) return;
    api
      .listRds(token)
      .then(setRds)
      .catch((e) => setError(String(e?.message ?? e)));
  };

  useEffect(reload, [token]);

  return (
    <div className="card overflow-hidden">
      {error && <div className="m-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      {rds === null ? (
        <p className="p-4 text-sm text-slate-500">Loading…</p>
      ) : rds.length === 0 ? (
        <p className="p-4 text-sm text-slate-500">No databases registered yet (Database management → Register RDS).</p>
      ) : (
        <table className="table-clean w-full">
          <thead>
            <tr>
              <th className="w-12">id</th>
              <th>database</th>
              <th>schemas</th>
              <th className="text-center">context</th>
              <th className="text-right">actions</th>
            </tr>
          </thead>
          <tbody>
            {rds.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 align-middle">
                <td className="font-mono text-[11px] text-slate-500">{r.id}</td>
                <td className="truncate max-w-[14rem]" title={r.display_name}>{r.display_name}</td>
                <td className="text-xs text-slate-500">{r.schemas.join(", ") || "—"}</td>
                <td className="text-center text-xs">
                  {r.has_context ? (
                    <span className="inline-flex rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 text-[10px]">set</span>
                  ) : (
                    <span className="text-slate-400">empty</span>
                  )}
                </td>
                <td className="text-right">
                  <button type="button" className="btn-primary text-xs py-1" onClick={() => setEditing(r)}>
                    edit context
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {token && editing && (
        <RdsContextModal token={token} rds={editing} onClose={() => setEditing(null)} onSaved={reload} />
      )}
    </div>
  );
}

type PreviewData = Awaited<ReturnType<typeof api.previewRdsContext>>;

function RdsContextModal({
  token,
  rds,
  onClose,
  onSaved,
}: {
  token: string;
  rds: RdsRegistration;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState("");
  const [mode, setMode] = useState("inherit"); // 'inherit' | 'full' | 'pointer'
  const [defaultMode, setDefaultMode] = useState("full");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .getRdsContext(token, rds.id)
      .then((r) => {
        if (cancelled) return;
        setValue(r.context_text ?? "");
        setMode(r.context_mode ?? "inherit");
        setDefaultMode(r.default_mode);
        setLoaded(true);
      })
      .catch((e: { message?: string }) => {
        if (!cancelled) setError(e?.message ?? String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [token, rds.id]);

  const loadPreview = async () => {
    setPreviewing(true);
    setError(null);
    try {
      setPreview(await api.previewRdsContext(token, rds.id));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewing(false);
    }
  };

  const onSave = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.putRdsContext(token, rds.id, {
        context_text: value,
        context_mode: mode === "inherit" ? "" : mode,
      });
      onSaved();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg border border-slate-200 shadow-xl max-w-3xl w-full max-h-[88vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Database context</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              <span className="font-mono">#{rds.id}</span> {rds.display_name} — schemas: {rds.schemas.join(", ") || "—"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="btn" disabled={busy}>
            Close
          </button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto space-y-3">
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700">Auto-inject mode for this database</label>
            <select className="input max-w-xs" value={mode} onChange={(e) => setMode(e.target.value)} disabled={!loaded}>
              <option value="inherit">Inherit default ({defaultMode})</option>
              <option value="full">Full — whole manual</option>
              <option value="pointer">Pointer only — short note</option>
            </select>
          </div>

          <textarea
            className="input font-mono text-xs min-h-[320px]"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={loaded ? "Schema, metric IDs, healing/patch rules, linking, query templates…" : "Loading…"}
            disabled={!loaded}
          />

          <div className="border-t border-slate-100 pt-3 space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              <button type="button" className="btn text-xs py-1" onClick={() => void loadPreview()} disabled={previewing}>
                {previewing ? "Loading…" : "Preview what Claude receives"}
              </button>
              {preview && (
                <span className="text-xs text-slate-500">
                  effective mode: <strong>{preview.effective_mode}</strong> · full ≈ {preview.full.chars.toLocaleString()}{" "}
                  chars / ~{preview.full.tokens_est.toLocaleString()} tok · pointer ≈ {preview.pointer.chars.toLocaleString()}{" "}
                  chars / ~{preview.pointer.tokens_est.toLocaleString()} tok ·{" "}
                  <strong>
                    active ≈ {preview.active.chars.toLocaleString()} chars / ~{preview.active.tokens_est.toLocaleString()} tok
                  </strong>
                </span>
              )}
            </div>
            {preview && (
              <pre className="text-[11px] font-mono whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded p-3 max-h-60 overflow-y-auto">
                {(preview.effective_mode === "full" ? preview.full.text : preview.pointer.text) || "(nothing injected)"}
              </pre>
            )}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50 rounded-b-lg">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={() => void onSave()} disabled={busy || !loaded}>
            {busy ? "Saving…" : "Save context"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Notice({ tone, children }: { tone: "ok" | "err"; children: React.ReactNode }) {
  return (
    <div
      className={`text-sm rounded px-3 py-2 border ${
        tone === "ok"
          ? "bg-emerald-50 text-emerald-800 border-emerald-200"
          : "bg-red-50 text-red-800 border-red-200"
      }`}
    >
      {children}
    </div>
  );
}

function SegBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 rounded-md font-medium transition-colors ${
        active ? "bg-white text-slate-900 shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-800"
      }`}
    >
      {children}
    </button>
  );
}
